import { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAuth } from '../../contexts/AuthContext';
import { BlogPost, BlogPostFormData, BlogPostStatus, BlogPostSEO } from '../../types/blog-admin';
import BlogPostForm from './BlogPostForm';
import { Button } from '../ui/button';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  FileText,
  Calendar,
  Tag,
  ExternalLink,
  CheckCircle2,
  Clock,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';

type ViewMode = 'list' | 'create' | 'edit';

export default function BlogManagement() {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | BlogPostStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'blogPosts'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BlogPost[];
      setPosts(postsData);
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (formData: BlogPostFormData, status: BlogPostStatus) => {
    if (!user) return;

    try {
      setSubmitting(true);

      const seo: BlogPostSEO = {
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        metaKeywords: formData.metaKeywords,
        focusKeyword: formData.focusKeyword,
        canonicalUrl: formData.canonicalUrl,
        ogTitle: formData.ogTitle,
        ogDescription: formData.ogDescription,
        ogImage: formData.ogImage,
        ogType: 'article',
        twitterCard: 'summary_large_image',
        twitterTitle: formData.twitterTitle,
        twitterDescription: formData.twitterDescription,
        twitterImage: formData.twitterImage
      };

      const newPost = {
        slug: formData.slug,
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt,
        author: formData.author,
        authorEmail: user.email || '',
        featuredImage: formData.featuredImage,
        tags: formData.tags,
        category: formData.category,
        readTime: formData.readTime,
        seo,
        status,
        publishedAt: status === 'published' ? Timestamp.now() : undefined,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        views: 0
      };

      await addDoc(collection(db, 'blogPosts'), newPost);

      toast.success(status === 'published' ? 'Blog post published!' : 'Blog post saved as draft');
      setViewMode('list');
      loadPosts();
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create blog post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePost = async (formData: BlogPostFormData, status: BlogPostStatus) => {
    if (!editingPost || !user) return;

    try {
      setSubmitting(true);

      const seo: BlogPostSEO = {
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        metaKeywords: formData.metaKeywords,
        focusKeyword: formData.focusKeyword,
        canonicalUrl: formData.canonicalUrl,
        ogTitle: formData.ogTitle,
        ogDescription: formData.ogDescription,
        ogImage: formData.ogImage,
        ogType: 'article',
        twitterCard: 'summary_large_image',
        twitterTitle: formData.twitterTitle,
        twitterDescription: formData.twitterDescription,
        twitterImage: formData.twitterImage
      };

      const updates: Partial<BlogPost> = {
        slug: formData.slug,
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt,
        author: formData.author,
        featuredImage: formData.featuredImage,
        tags: formData.tags,
        category: formData.category,
        readTime: formData.readTime,
        seo,
        status,
        updatedAt: Timestamp.now()
      };

      // Set publishedAt only when first publishing
      if (status === 'published' && editingPost.status !== 'published') {
        updates.publishedAt = Timestamp.now();
      }

      const postRef = doc(db, 'blogPosts', editingPost.id);
      await updateDoc(postRef, updates);

      toast.success('Blog post updated!');
      setViewMode('list');
      setEditingPost(null);
      loadPosts();
    } catch (error) {
      console.error('Error updating post:', error);
      toast.error('Failed to update blog post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'blogPosts', postId));
      toast.success('Blog post deleted');
      loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete blog post');
    }
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setViewMode('edit');
  };

  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesStatus = filterStatus === 'all' || post.status === filterStatus;
    const matchesSearch = searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  if (viewMode === 'create') {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Create New Blog Post</h1>
          <p className="text-muted-foreground">
            Create SEO-optimized content to drive organic traffic
          </p>
        </div>
        <BlogPostForm
          onSubmit={handleCreatePost}
          onCancel={() => setViewMode('list')}
          isLoading={submitting}
        />
      </div>
    );
  }

  if (viewMode === 'edit' && editingPost) {
    const formData: Partial<BlogPostFormData> = {
      slug: editingPost.slug,
      title: editingPost.title,
      content: editingPost.content,
      excerpt: editingPost.excerpt,
      author: editingPost.author,
      tags: editingPost.tags,
      category: editingPost.category,
      readTime: editingPost.readTime,
      featuredImage: editingPost.featuredImage,
      metaDescription: editingPost.seo.metaDescription,
      metaKeywords: editingPost.seo.metaKeywords,
      focusKeyword: editingPost.seo.focusKeyword,
      canonicalUrl: editingPost.seo.canonicalUrl,
      metaTitle: editingPost.seo.metaTitle,
      ogTitle: editingPost.seo.ogTitle,
      ogDescription: editingPost.seo.ogDescription,
      ogImage: editingPost.seo.ogImage,
      twitterTitle: editingPost.seo.twitterTitle,
      twitterDescription: editingPost.seo.twitterDescription,
      twitterImage: editingPost.seo.twitterImage,
      status: editingPost.status
    };

    return (
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Edit Blog Post</h1>
          <p className="text-muted-foreground">
            Update your blog post and SEO settings
          </p>
        </div>
        <BlogPostForm
          initialData={formData}
          onSubmit={handleUpdatePost}
          onCancel={() => {
            setViewMode('list');
            setEditingPost(null);
          }}
          isLoading={submitting}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Blog Management</h1>
          <p className="text-muted-foreground">
            Manage your SEO-optimized blog content
          </p>
        </div>
        <Button onClick={() => setViewMode('create')}>
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
          />
        </div>

        {/* Status Filter */}
        <div className="flex gap-2">
          {(['all', 'published', 'draft', 'archived'] as const).map((status) => (
            <Button
              key={status}
              variant={filterStatus === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus(status)}
            >
              {status === 'all' && 'All'}
              {status === 'published' && (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Published
                </>
              )}
              {status === 'draft' && (
                <>
                  <Clock className="h-4 w-4 mr-1" />
                  Drafts
                </>
              )}
              {status === 'archived' && (
                <>
                  <Archive className="h-4 w-4 mr-1" />
                  Archived
                </>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="text-center py-16">
          <p className="text-muted-foreground">Loading blog posts...</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">
            {searchQuery || filterStatus !== 'all'
              ? 'No posts match your filters'
              : 'No blog posts yet'}
          </p>
          {searchQuery === '' && filterStatus === 'all' && (
            <Button onClick={() => setViewMode('create')}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Post
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPosts.map((post) => (
            <div
              key={post.id}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                {/* Featured Image Thumbnail */}
                {post.featuredImage && (
                  <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-muted">
                    <img
                      src={post.featuredImage.url}
                      alt={post.featuredImage.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Title & Status */}
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold mb-1">{post.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {post.excerpt || post.seo.metaDescription}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {post.status === 'published' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-500 rounded text-xs font-medium">
                          <CheckCircle2 className="h-3 w-3" />
                          Published
                        </span>
                      )}
                      {post.status === 'draft' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded text-xs font-medium">
                          <Clock className="h-3 w-3" />
                          Draft
                        </span>
                      )}
                      {post.status === 'archived' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted text-muted-foreground rounded text-xs font-medium">
                          <Archive className="h-3 w-3" />
                          Archived
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Meta Info */}
                  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {post.publishedAt
                        ? new Date(post.publishedAt.toMillis()).toLocaleDateString()
                        : 'Not published'}
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      {post.readTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {post.views} views
                    </span>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {post.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs"
                      >
                        <Tag className="h-3 w-3" />
                        {tag}
                      </span>
                    ))}
                    {post.tags.length > 3 && (
                      <span className="text-xs text-muted-foreground">
                        +{post.tags.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* SEO Info */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Focus:</span>
                    <span className="font-mono bg-muted px-2 py-0.5 rounded">
                      {post.seo.focusKeyword}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  {post.status === 'published' && (
                    <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">
                      <Button
                        variant="ghost"
                        size="sm"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </a>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditPost(post)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeletePost(post.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
